import React, { useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import TitleIcon from '@mui/icons-material/Title';
import { brandColors } from '@config/brandTokens';
import { ContestRichText } from '@components/contest/ContestSectionBlocks';
import {
  formatContestSectionBlockNumber,
  newContestSectionBlock,
} from '@utils/contestCatalog';
import type { ContestSectionBlock } from '../../types/contestCatalog';

function applyFormat(
  value: string,
  field: HTMLTextAreaElement | null,
  kind: 'bold' | 'italic' | 'heading' | 'list',
): string {
  const start = field?.selectionStart ?? value.length;
  const end = field?.selectionEnd ?? value.length;
  const selected = value.slice(start, end);
  if (kind === 'heading') {
    const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const prefix = value.slice(lineStart, start).startsWith('## ') ? '' : '## ';
    return value.slice(0, lineStart) + prefix + value.slice(lineStart);
  }
  if (kind === 'list') {
    const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const prefix = value.slice(lineStart, start).startsWith('- ') ? '' : '- ';
    return value.slice(0, lineStart) + prefix + value.slice(lineStart);
  }
  const wrap = kind === 'bold' ? '**' : '*';
  const inner = selected || (kind === 'bold' ? 'bold' : 'italic');
  return `${value.slice(0, start)}${wrap}${inner}${wrap}${value.slice(end)}`;
}

const AdminFrame2BlockEditor: React.FC<{
  blocks: ContestSectionBlock[];
  onChange: (next: ContestSectionBlock[]) => void;
  sectionIndex?: number;
}> = ({ blocks, onChange, sectionIndex = 0 }) => {
  const bodyRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const items = blocks.length ? blocks : [newContestSectionBlock()];

  const updateBlock = (id: string, patch: Partial<ContestSectionBlock>) => {
    const source = blocks.length ? blocks : items;
    onChange(source.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  return (
    <Stack spacing={1.25}>
      {items.map((block, index) => {
        const numberLabel = formatContestSectionBlockNumber(sectionIndex, index);
        const images = block.images || [];
        return (
          <Paper key={block.id || numberLabel} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={1.1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {numberLabel}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`Remove ${numberLabel}`}
                  disabled={items.length <= 1}
                  onClick={() => onChange(items.filter((row) => row.id !== block.id))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                label={`${numberLabel} heading`}
                size="small"
                fullWidth
                value={block.title || ''}
                onChange={(e) => updateBlock(block.id, { title: e.target.value })}
              />
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Bold **text**">
                  <IconButton
                    size="small"
                    onClick={() =>
                      updateBlock(block.id, {
                        body: applyFormat(block.body || '', bodyRefs.current[block.id], 'bold'),
                      })
                    }
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic *text*">
                  <IconButton
                    size="small"
                    onClick={() =>
                      updateBlock(block.id, {
                        body: applyFormat(block.body || '', bodyRefs.current[block.id], 'italic'),
                      })
                    }
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Heading">
                  <IconButton
                    size="small"
                    onClick={() =>
                      updateBlock(block.id, {
                        body: applyFormat(block.body || '', bodyRefs.current[block.id], 'heading'),
                      })
                    }
                  >
                    <TitleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bullet list">
                  <IconButton
                    size="small"
                    onClick={() =>
                      updateBlock(block.id, {
                        body: applyFormat(block.body || '', bodyRefs.current[block.id], 'list'),
                      })
                    }
                  >
                    <FormatListBulletedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TextField
                label={`${numberLabel} text`}
                fullWidth
                multiline
                minRows={4}
                value={block.body || ''}
                inputRef={(el) => {
                  bodyRefs.current[block.id] = el;
                }}
                onChange={(e) => updateBlock(block.id, { body: e.target.value })}
                helperText="Paragraphs on separate lines. Use the toolbar for bold, italic, headings, and bullets."
              />
              {String(block.body || '').trim() ? (
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1.5, p: 1.25, border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: brandColors.slate, display: 'block', mb: 0.5 }}>
                    Preview
                  </Typography>
                  <ContestRichText text={block.body} />
                </Box>
              ) : null}
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" sx={{ fontWeight: 700, color: brandColors.slate }}>
                    Images
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      updateBlock(block.id, { images: [...images, { url: '', caption: '' }] })
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Add image
                  </Button>
                </Stack>
                {images.map((image, imageIndex) => (
                  <Stack key={`${block.id}-img-${imageIndex}`} spacing={0.75}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                      <TextField
                        label="Image URL"
                        size="small"
                        fullWidth
                        value={image.url}
                        onChange={(e) =>
                          updateBlock(block.id, {
                            images: images.map((row, idx) =>
                              idx === imageIndex ? { ...row, url: e.target.value } : row,
                            ),
                          })
                        }
                      />
                      <TextField
                        label="Caption"
                        size="small"
                        fullWidth
                        value={image.caption || ''}
                        onChange={(e) =>
                          updateBlock(block.id, {
                            images: images.map((row, idx) =>
                              idx === imageIndex ? { ...row, caption: e.target.value } : row,
                            ),
                          })
                        }
                      />
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove image"
                        onClick={() =>
                          updateBlock(block.id, {
                            images: images.filter((_, idx) => idx !== imageIndex),
                          })
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    {/^https?:\/\//i.test(image.url.trim()) || image.url.trim().startsWith('/') ? (
                      <Box
                        component="img"
                        src={image.url.trim()}
                        alt={image.caption || 'Frame 2'}
                        sx={{
                          maxWidth: 280,
                          maxHeight: 140,
                          borderRadius: 1,
                          border: '1px solid #e2e8f0',
                        }}
                      />
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Paper>
        );
      })}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...items, newContestSectionBlock()])}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        Add {formatContestSectionBlockNumber(sectionIndex, items.length)}
      </Button>
    </Stack>
  );
};

export default AdminFrame2BlockEditor;
